import DraftIcon from '@/components/icons/draft';
import { mobileWidth } from '@/constants';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '@/contexts/userContext';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useViewingTagSelection } from '@/hooks/useViewingTagSelection';
import {
  type VaultFileAccount,
  useGetVaultFileAccountsQuery,
} from '@/store/accountsApi';
import {
  Document2Record,
  useDownloadDocumentMutation,
  useGetDocuments2Query,
  usePreviewDocumentMutation,
} from '@/store/documentsApi';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import dayjs from '@/config/dayjs';
import { useLocalization } from '@/hooks/useLocalization';
import AdvisorTableHeader from '../../advisorTableHeader';
import { DocVaultGrid } from './DocVaultGrid';
import { VaultEmptyOverlay, VaultListState } from './VaultListState';
import { getQueriesToRetry, getVaultListStatus } from './vaultListStatus';
import DocumentVaultTagEmptyState from './DocumentVaultTagEmptyState';
import { DataRangeFilterEnum } from '@/constants/dates';
import {
  getDocumentDownloadName,
  getDocumentFilenameForBlobHandling,
  getHistoricalTaxStatementId,
  hasActiveDocumentVaultTag,
  normalizeDocument2Records,
  normalizeVaultFileAccounts,
  shouldShowDocumentVaultTagEmptyState,
  skeletonRows,
} from './documentVaultGridUtils';
import {
  taxDocumentsVaultDateFilterOptions,
  toTaxDocumentsDateIntervalApiValue,
} from './taxDocumentsVaultGridConfig';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import {
  AdvisorFeedAccountSelect,
  AdvisorFeedDateSelect,
} from '@/modules/advisor/documentVault/AdvisorFeedFilters';
import {
  TAX_VAULT_CATEGORY_ID,
  advisorFeedDocuments2WealthAccountId,
  applyAdvisorFeedRows,
  filterFeedRowsByEntitledAccounts,
  formatFeedStatementDate,
  toAdvisorTaxDateIntervalApiValue,
} from '@/modules/advisor/documentVault/advisorFeedTab';

// Define source options
enum FileSource {
  All = '',
  Trust = '1',
  Banking = '2',
  Brokerage = '3',
}

interface TaxDocumentsGridProps {
  searchTerm?: string;
  wealthAccountId?: number;
  vaultFileCategoryId?: number;
  isWidget?: boolean;
  investorId?: number;
  dateFilter?: DataRangeFilterEnum;
  onDateFilterChange?: (filter: DataRangeFilterEnum) => void;
  paginated?: boolean;
}

const TaxDocumentsGrid = ({
  searchTerm,
  wealthAccountId,
  vaultFileCategoryId,
  investorId,
  dateFilter: controlledDateFilter,
  onDateFilterChange,
  paginated = false,
}: TaxDocumentsGridProps) => {
  const { currentRole } = useContext(UserContext);
  const isAdvisor = (currentRole || '').toLowerCase() === 'advisor';
  const isAccountView = wealthAccountId !== undefined;
  const isAdvisorFeed = isAdvisor && !isAccountView;
  const showTagSelectorDropdown = useShowTagSelectorDropdown();
  const viewingTagSelection = useViewingTagSelection();
  const [selectedSource, setSelectedSource] = useState<FileSource>(
    FileSource.All,
  );
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
  const [localDateFilter, setLocalDateFilter] = useState<DataRangeFilterEnum>(
    DataRangeFilterEnum.Trailing1Year,
  );
  const dateFilter = controlledDateFilter ?? localDateFilter;
  const setDateFilter = (next: DataRangeFilterEnum) => {
    onDateFilterChange?.(next);
    if (controlledDateFilter === undefined) {
      setLocalDateFilter(next);
    }
  };
  const hasSelectedViewingTag = hasActiveDocumentVaultTag(
    showTagSelectorDropdown,
    viewingTagSelection,
    investorId,
  );

  const handleSourceChange = (event: SelectChangeEvent) => {
    setSelectedSource(event.target.value as FileSource);
  };

  const handleAccountChange = (event: SelectChangeEvent) => {
    setSelectedAccount(event.target.value);
  };

  const { t } = useLocalization();
  const isMobile = useMediaQuery(mobileWidth);

  const columns: GridColDef<Document2Record>[] = useMemo(() => {
    const formatDate = (value: string | null | undefined) =>
      dayjs(value).isValid()
        ? dayjs(value).format('MM/DD/YYYY')
        : 'Invalid Date';
    const fileColumn: GridColDef<Document2Record> = {
      field: 'Name',
      headerName: t('grid.file'),
      flex: 1,
      renderCell: ({ row }) => (
        <Stack
          flexDirection='row'
          alignItems='center'
          gap={1}
        >
          <DraftIcon /> {row.Name}
        </Stack>
      ),
    };
    const lastUpdatedColumn: GridColDef<Document2Record> = {
      field: 'UploadDate',
      headerName: t('grid.lastUpdated'),
      width: 144,
      valueFormatter: formatDate,
    };
    const statementDateColumn: GridColDef<Document2Record> = {
      field: 'StatementDate',
      headerName: t('grid.statementDate'),
      width: 144,
      valueFormatter: isAdvisorFeed
        ? (value, row) => formatFeedStatementDate(value, row.UploadDate)
        : formatDate,
    };
    return isAdvisorFeed
      ? [fileColumn, statementDateColumn, lastUpdatedColumn]
      : [fileColumn, lastUpdatedColumn, statementDateColumn];
  }, [isAdvisorFeed, t]);

  const sourceOptions = useMemo(
    () => [
      { value: FileSource.All, label: t('grid.allSources') },
      { value: FileSource.Trust, label: t('grid.trust') },
      { value: FileSource.Banking, label: t('grid.banking') },
      { value: FileSource.Brokerage, label: t('grid.brokerage') },
    ],
    [t],
  );

  // Handle date interval dropdown change
  const handleDateFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value as DataRangeFilterEnum);
  };

  const isAuthLoaded = useAuthIsLoaded();
  const {
    data: advisorGroupSettings,
    isLoading: isLoadingAdvisorGroupSettingsRaw,
    isError: isAdvisorGroupSettingsError,
    error: errorAdvisorGroupSettings,
  } = useGetAdvisorGroupSettingsQuery(undefined, {
    skip: !isAuthLoaded || !isAdvisorFeed,
  });
  const isLoadingAdvisorGroupSettings = isLoadingAdvisorGroupSettingsRaw;
  const requireTaxDocumentEntitlements = AdvisorGroupSettings(
    advisorGroupSettings,
  ).getByKey('RequireTaxDocumentEntitlements');
  const apiRef = useGridApiRef(); // Create apiRef

  const [previewDocument, { isLoading: isPreviewing }] =
    usePreviewDocumentMutation();
  const [downloadDocument, { isLoading: isDownloading }] =
    useDownloadDocumentMutation();

  const isAccountsQuerySkipped =
    !isAuthLoaded || isAccountView || (isAdvisor && !investorId);
  const {
    currentData: accountsData,
    isLoading: isLoadingAccountsRaw,
    isFetching: isFetchingAccountsRaw,
    isError: isAccountsError,
    isUninitialized: isAccountsUninitialized,
    error: errorAccounts,
    refetch: refetchAccounts,
  } = useGetVaultFileAccountsQuery(
    {
      vaultFileCategoryId: vaultFileCategoryId ?? 2,
      advisorInvestorId: investorId,
    },
    {
      skip: isAccountsQuerySkipped,
    },
  );

  const vaultFileAccounts: VaultFileAccount[] = useMemo(
    () => normalizeVaultFileAccounts(accountsData),
    [accountsData],
  );

  const handleRowAction = async (
    action: 'preview' | 'download' | 'folderClick',
    data: Document2Record,
  ) => {
    // Trust the action from the child component, no need to re-evaluate.
    const statementId = getHistoricalTaxStatementId(data);
    const statementName = statementId
      ? getDocumentDownloadName(data)
      : undefined;

    if (action === 'preview') {
      await previewDocument({
        vaultId: data.VaultFile2Id,
        statementId,
        statementName,
        InvestorId: investorId,
        fileName: getDocumentFilenameForBlobHandling(data),
      }).unwrap();
      return;
    }

    if (action === 'download') {
      await downloadDocument({
        vaultId: data.VaultFile2Id,
        filename: getDocumentDownloadName(data),
        statementId,
        statementName,
        InvestorId: investorId,
      }).unwrap();
      return;
    }
  };

  const isLoadingAccounts = Boolean(isLoadingAccountsRaw);
  const skipTaxDocuments =
    !isAuthLoaded ||
    (isAdvisorFeed && isLoadingAccounts) ||
    (isAdvisorFeed && isLoadingAdvisorGroupSettings) ||
    (isAdvisor && !isAccountView && !investorId);

  const documents2WealthAccountId = useMemo(
    () =>
      advisorFeedDocuments2WealthAccountId({
        isAccountView,
        wealthAccountId,
        selectedAccount,
        selectedAccountIds,
      }),
    [isAccountView, selectedAccount, selectedAccountIds, wealthAccountId],
  );

  const {
    currentData: taxDocsData,
    isLoading: isLoadingDocsRaw,
    isFetching: isFetchingDocsRaw,
    isError: isDocumentsError,
    isUninitialized: isDocumentsUninitialized,
    error: errorDocs,
    refetch: refetchDocuments,
  } = useGetDocuments2Query(
    {
      VaultFileCategoryId: vaultFileCategoryId ?? TAX_VAULT_CATEGORY_ID,
      DateInterval: isAdvisorFeed
        ? toAdvisorTaxDateIntervalApiValue(dateFilter)
        : toTaxDocumentsDateIntervalApiValue(dateFilter),
      WealthAccountId: documents2WealthAccountId,
      ...(!isAdvisorFeed && selectedSource !== FileSource.All
        ? { FileSourceId: selectedSource }
        : {}),
      SearchTerm: searchTerm,
      AdvisorInvestorId: investorId,
    },
    { skip: skipTaxDocuments },
  );

  const rows = useMemo(() => {
    const taxRows = normalizeDocument2Records(taxDocsData);
    if (!isAdvisorFeed) {
      return taxRows;
    }
    const entitledRows = requireTaxDocumentEntitlements
      ? filterFeedRowsByEntitledAccounts(taxRows, vaultFileAccounts)
      : taxRows;
    return applyAdvisorFeedRows(entitledRows, dateFilter, selectedAccountIds);
  }, [
    dateFilter,
    isAdvisorFeed,
    requireTaxDocumentEntitlements,
    selectedAccountIds,
    taxDocsData,
    vaultFileAccounts,
  ]);

  const isLoadingDocs = Boolean(isLoadingDocsRaw);
  const isFetchingDocs = Boolean(isFetchingDocsRaw);
  const isLoading =
    isLoadingAccounts ||
    isLoadingDocs ||
    (isAdvisorFeed && isLoadingAdvisorGroupSettings);
  const isFetching = isFetchingDocs;
  const accountsQuery = {
    isLoading: isLoadingAccounts,
    isFetching: Boolean(isFetchingAccountsRaw),
    isError: Boolean(isAccountsError),
    isUninitialized: Boolean(isAccountsUninitialized),
    hasData: Boolean(accountsData),
    skip: isAccountsQuerySkipped,
  };
  const documentsQuery = {
    isLoading: isLoadingDocs,
    isFetching,
    isError: Boolean(isDocumentsError),
    isUninitialized: Boolean(isDocumentsUninitialized),
    hasData: Boolean(taxDocsData),
    skip: skipTaxDocuments && !isLoadingAdvisorGroupSettings,
  };
  const settingsQuery = {
    isLoading: isLoadingAdvisorGroupSettings,
    isFetching: false,
    isError: isAdvisorGroupSettingsError,
    isUninitialized: false,
    hasData: Boolean(advisorGroupSettings) && !isAdvisorGroupSettingsError,
    skip: !isAdvisorFeed,
  };
  const listQueries = [
    documentsQuery,
    settingsQuery,
    ...(requireTaxDocumentEntitlements ? [accountsQuery] : []),
  ];
  const vaultListStatus = getVaultListStatus(listQueries);
  const showAccountsError =
    paginated &&
    !accountsQuery.skip &&
    accountsQuery.isError &&
    !accountsQuery.hasData;
  const handleRetry = () => {
    const failed = getQueriesToRetry(listQueries);
    if (failed.includes(documentsQuery)) {
      void refetchDocuments();
    }
    if (failed.includes(accountsQuery)) {
      void refetchAccounts();
    }
  };
  const handleAccountsRetry = () => {
    if (getQueriesToRetry([accountsQuery]).length > 0) {
      void refetchAccounts();
    }
  };
  const tagKey = viewingTagSelection
    ? `${viewingTagSelection.source}:${String(viewingTagSelection.tagId)}`
    : '';
  const paginationResetKey = `${String(investorId ?? '')}|${selectedAccount}|${selectedAccountIds.join(',')}|${selectedSource}|${dateFilter}|${searchTerm ?? ''}|${tagKey}`;
  const shouldShowTagEmptyState = shouldShowDocumentVaultTagEmptyState({
    hasSelectedTag: hasSelectedViewingTag,
    searchTerm,
    isLoading,
    isFetching,
    rowCount: rows.length,
    isQuerySkipped: isAdvisor && !isAccountView && !investorId,
    isAccountView,
    accountCount: vaultFileAccounts.length,
  });

  useEffect(() => {
    if (
      selectedAccount !== '' &&
      !vaultFileAccounts.some(
        (account) => String(account.WealthAccountId) === selectedAccount,
      )
    ) {
      setSelectedAccount('');
    }
  }, [vaultFileAccounts, selectedAccount]);

  useEffect(() => {
    setSelectedAccountIds((current) => {
      const next = current.filter((id) =>
        vaultFileAccounts.some((account) => account.WealthAccountId === id),
      );
      return next.length === current.length ? current : next;
    });
  }, [vaultFileAccounts]);

  // Apply filter based on searchTerm
  useEffect(() => {
    const gridApi = apiRef.current;
    if (!gridApi) {
      return;
    }

    if (searchTerm) {
      gridApi.setFilterModel({
        items: [
          {
            field: 'Name',
            operator: 'contains',
            value: searchTerm,
          },
        ],
      });
    } else {
      gridApi.setFilterModel({ items: [] });
    }
  }, [searchTerm, apiRef]);

  if (
    !paginated &&
    isAdvisorFeed &&
    ((isLoadingAccounts && !accountsData) || isLoadingAdvisorGroupSettings)
  ) {
    return (
      <Stack
        gap={1}
        px={1}
      >
        <Stack
          direction='row'
          justifyContent='flex-start'
          alignItems='center'
          spacing={2}
          sx={{
            p: 1,
            marginTop: '40px !important',
            marginLeft: '0px !important',
            paddingLeft: '0px !important',
          }}
        >
          <Skeleton
            variant='rectangular'
            height={40}
            width={180}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
            }}
          >
            <Skeleton
              variant='rectangular'
              height={40}
              width={180}
            />
            <Skeleton
              variant='rectangular'
              height={40}
              width={GRID_COLUMN_WIDTH_NARROW}
            />
          </Box>
        </Stack>
        {skeletonRows.map((index) => (
          <Skeleton
            key={index}
            variant='rectangular'
            height={52}
          />
        ))}
      </Stack>
    );
  }

  // Use Skeleton during loading
  if (
    !paginated &&
    !isAdvisorFeed &&
    (isLoading || isFetching) &&
    !taxDocsData
  ) {
    return (
      <Stack
        gap={1}
        px={1}
      >
        {/* Add skeleton for the new dropdown area */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent='flex-start'
          alignItems={isMobile ? 'stretch' : 'center'}
          spacing={2}
          sx={{
            p: 1,
            marginTop: '40px !important',
            marginLeft: '0px !important',
            paddingLeft: '0px !important',
          }}
        >
          <div />
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 4,
              marginLeft: '0px !important',
              justifyContent: 'space-between',
            }}
          >
            <Skeleton
              variant='rectangular'
              height={40}
              width={isMobile ? '100%' : 180}
            />
            <Skeleton
              variant='rectangular'
              height={40}
              width={isMobile ? '100%' : GRID_COLUMN_WIDTH_NARROW}
            />
          </Box>
        </Stack>
        {skeletonRows.map((index) => (
          <Skeleton
            key={index}
            variant='rectangular'
            height={52}
          />
        ))}
      </Stack>
    );
  }

  if (
    !paginated &&
    isAdvisorFeed &&
    (errorDocs || isAdvisorGroupSettingsError)
  ) {
    console.error(
      'Error loading tax documents',
      errorDocs ?? errorAdvisorGroupSettings,
    );
    return (
      <Alert severity='error'>
        {t('documentVault.feedLoadError', 'Unable to load documents.')}
      </Alert>
    );
  }

  if (!paginated && !isAdvisorFeed && errorAccounts) {
    console.error('Error loading tax document accounts:', errorAccounts);
    return <Typography color='error'>Error loading accounts.</Typography>;
  }

  if (!paginated && errorDocs) {
    console.error('Error loading tax documents:', errorDocs);
    return <Typography color='error'>Error loading tax documents.</Typography>;
  }

  const filters = (
    <>
      {isAdvisor && !isAdvisorFeed && <AdvisorTableHeader />}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent='flex-start'
        alignItems={isMobile ? 'stretch' : 'center'}
        spacing={2}
        sx={{
          p: 1,
          width: '100%',
          marginTop: '0px !important',
          marginLeft: '0px !important',
          paddingLeft: '0px !important',
        }}
      >
        {isAdvisorFeed ? (
          <Typography
            variant='h6'
            sx={{ flex: 1 }}
          >
            Recently Uploaded
          </Typography>
        ) : (
          !isMobile && <div />
        )}

        {/* Dropdowns (match Statements layout: start on the left) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 4,
            marginLeft: '0px !important',
            justifyContent: 'space-between',
            '& .MuiFormControl-root': {
              marginLeft: '0px',
              marginRight: '0px',
              ...(isMobile && { width: '100%' }),
            },
          }}
        >
          {isAdvisorFeed ? (
            <>
              <AdvisorFeedAccountSelect
                idPrefix='tax'
                accounts={vaultFileAccounts}
                selectedAccountIds={selectedAccountIds}
                onChange={setSelectedAccountIds}
                loadFailed={!paginated && Boolean(errorAccounts)}
              />
              {showAccountsError && (
                <FormHelperText error>
                  {t('documentVault.accountsLoadError')}
                  <Button
                    size='small'
                    onClick={handleAccountsRetry}
                  >
                    {t('retry')}
                  </Button>
                </FormHelperText>
              )}
            </>
          ) : (
            !isAccountView && (
              <FormControl
                sx={{ minWidth: 180 }}
                size='small'
              >
                <InputLabel
                  id='tax-account-select-label'
                  shrink
                >
                  {t('account')}
                </InputLabel>
                <Select
                  labelId='tax-account-select-label'
                  id='tax-account-select'
                  value={selectedAccount}
                  label={t('account')}
                  onChange={handleAccountChange}
                  displayEmpty
                  disabled
                >
                  <MenuItem value=''>{t('viewingTag.allAccounts')}</MenuItem>
                  {vaultFileAccounts.map((account) => (
                    <MenuItem
                      key={account.WealthAccountId}
                      value={account.WealthAccountId}
                    >
                      {account.Name} - ({account.AccountNumber})
                    </MenuItem>
                  ))}
                </Select>
                {showAccountsError && (
                  <FormHelperText error>
                    {t('documentVault.accountsLoadError')}
                    <Button
                      size='small'
                      onClick={handleAccountsRetry}
                    >
                      {t('retry')}
                    </Button>
                  </FormHelperText>
                )}
              </FormControl>
            )
          )}

          {!isAdvisorFeed && (
            <FormControl
              sx={{ minWidth: 180 }}
              size='small'
            >
              <InputLabel
                id='tax-source-select-label'
                shrink
              >
                {t('grid.source')}
              </InputLabel>
              <Select
                labelId='tax-source-select-label'
                id='tax-source-select'
                value={selectedSource}
                label={t('grid.source')}
                onChange={handleSourceChange}
                displayEmpty
              >
                {sourceOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isAdvisorFeed ? (
            <AdvisorFeedDateSelect
              idPrefix='tax'
              value={dateFilter}
              onChange={setDateFilter}
            />
          ) : (
            <FormControl
              sx={{ minWidth: GRID_COLUMN_WIDTH_NARROW }}
              size='small'
            >
              <InputLabel
                id='tax-date-interval-select-label'
                shrink
              >
                {t('date')}
              </InputLabel>
              <Select
                labelId='tax-date-interval-select-label'
                id='tax-date-interval-select'
                value={dateFilter}
                label={t('date')}
                displayEmpty
                onChange={handleDateFilterChange}
              >
                {taxDocumentsVaultDateFilterOptions.map((option) => (
                  <MenuItem
                    key={option.filter}
                    value={option.filter}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Stack>
    </>
  );

  const grid = (
    <Box>
      <Box>
        {shouldShowTagEmptyState ? (
          <DocumentVaultTagEmptyState />
        ) : (
          <DocVaultGrid
            columns={columns}
            rows={rows}
            loading={isLoading || isFetching || isPreviewing || isDownloading}
            checkboxSelection
            disableRowSelectionOnClick
            rowHeight={52}
            apiRef={apiRef}
            onRowAction={handleRowAction}
            investorId={investorId}
            bulkDownloadContextName='tax-documents'
            showBulkActions={false}
            useDocuments2BulkDownload
            enableXsListView
            {...(paginated && {
              paginated: true,
              paginationResetKey,
              slots: {
                noRowsOverlay: VaultEmptyOverlay,
                noResultsOverlay: VaultEmptyOverlay,
              },
            })}
            {...(isAdvisorFeed
              ? {
                  slotProps: {
                    loadingOverlay: {
                      variant: 'linear-progress' as const,
                      noRowsVariant: 'skeleton' as const,
                    },
                  },
                  localeText: {
                    noRowsLabel: t('noDataToDisplay', 'No data to display'),
                  },
                  initialState: {
                    sorting: {
                      sortModel: [
                        { field: 'StatementDate', sort: 'desc' as const },
                      ],
                    },
                  },
                }
              : {})}
            sx={{
              height: '100%',
              border: 'none',
              '& .MuiDataGrid-virtualScroller': {
                overflow: 'auto',
              },
              '& .MuiDataGrid-scrollbar': {
                display: 'block',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        )}
      </Box>
      {!paginated && !shouldShowTagEmptyState && (
        <Box
          sx={{
            mt: 2,
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography variant='subtitle1'>Total Documents</Typography>
          <Typography variant='subtitle1'>{rows.length}</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Stack
      gap={2}
      flex={1}
      overflow='hidden'
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {filters}
      {paginated ? (
        <VaultListState
          status={vaultListStatus}
          onRetry={handleRetry}
        >
          {grid}
        </VaultListState>
      ) : (
        grid
      )}
    </Stack>
  );
};

export default TaxDocumentsGrid;
